<template>
  <div class="main">
    <!-- -->
    <div class="content">
      <div class="title">{{ $t('dev.title') }}</div>
      <div class="appKey">
        <div class="flex option">
          <div>{{ $t('dev.appkey_label') }}</div>
          <div class="btns">
            <el-button type="primary" size="mini" @click="openVerification('view')"
              >{{ $t('public.copy') }}</el-button
            >
          </div>
        </div>
        <div class="info">
          {{ $t('dev.appkey_desc') }}
        </div>
      </div>
      <div class="secret">
        <div class="flex option">
          <div>{{ $t('dev.secret_label') }}</div>
          <div class="btns">
            <el-button type="primary" size="mini" @click="openVerification('view')"
              >{{ $t('public.copy') }}</el-button
            >
            <el-button size="mini" @click="openVerification('reset')">{{ $t('public.reset') }}</el-button>
          </div>
        </div>
        <div class="info">
          {{ $t('dev.secret_desc') }}
        </div>
      </div>
    </div>
    <el-dialog
      :title="$t('dev.dialog_title')"
      :visible.sync="visible"
      width="460px"
      custom-class="secret-dialog"
      :close-on-click-modal="false"
      @close="clearSecrets"
    >
      <template v-if="!verified">
        <div v-if="action === 'reset'" class="tip">
          <i class="el-icon-warning-outline"></i>
          <span>{{ $t('dev.reset_tip') }}</span>
        </div>
        <div class="fields">
          <el-input
            v-model="verification.userPwd"
            type="password"
            autocomplete="new-password"
            prefix-icon="el-icon-lock"
            :placeholder="$t('dev.cur_pwd_ph')"
            maxlength="128"
          />
          <div class="captcha-row">
            <el-input
              v-model="verification.captchaVal"
              prefix-icon="el-icon-key"
              :placeholder="$t('dev.captcha_ph')"
              maxlength="8"
              @keyup.enter.native="verify"
            />
            <div class="captcha-box" :title="$t('dev.captcha_alt')" @click="loadCaptcha">
              <img v-if="captchaImage" :src="captchaImage" :alt="$t('dev.captcha_alt')" />
              <span v-else>{{ $t('dev.refresh_captcha') }}</span>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="reveal">
          <div class="reveal-item">
            <span class="reveal-label">{{ $t('dev.appkey_show') }}</span>
            <code class="reveal-value">{{ appKey }}</code>
            <el-button class="reveal-copy" icon="el-icon-document-copy" circle :title="$t('dev.copy_appkey')" @click="copy('appKey')" />
          </div>
          <div class="reveal-item">
            <span class="reveal-label">{{ $t('dev.secret_show') }}</span>
            <code class="reveal-value">{{ secret }}</code>
            <el-button class="reveal-copy" icon="el-icon-document-copy" circle :title="$t('dev.copy_secret')" @click="copy('secret')" />
          </div>
        </div>
      </template>
      <span slot="footer">
        <el-button @click="visible = false">{{ $t('public.close') }}</el-button>
        <el-button v-if="!verified" type="primary" :loading="loading" @click="verify">{{ action === 'reset' ? $t('dev.verify_reset') : $t('dev.verify_view') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>
  
  <script>
import { resetSecret, getDevSecret, getCode } from '@/api/index';
import { sessionSnapshot, isCurrentSession } from '@/utils/session';
export default {
  name: "OpenWebDevelopApp",

  data() {
    return {
      appKey: '', secret: '', visible: false, verified: false, loading: false,
      action: 'view', revision: 0, captchaRevision: 0, captchaImage: '', snapshot: null,
      verification: { userPwd: '', captchaId: '', captchaVal: '' },
    };
  },

  beforeDestroy() { this.clearSecrets(); },
  deactivated() { this.clearSecrets(); },
  beforeRouteLeave(to, from, next) { this.clearSecrets(); next(); },

  methods: {
    clearSecrets() {
      this.revision++;
      this.captchaRevision++;
      this.appKey = this.secret = this.captchaImage = '';
      this.verification = { userPwd: '', captchaId: '', captchaVal: '' };
      this.verified = this.loading = false;
      this.snapshot = null;
    },
    openVerification(action) {
      this.clearSecrets();
      this.action = action;
      this.snapshot = sessionSnapshot();
      this.visible = true;
      this.loadCaptcha();
    },
    async loadCaptcha() {
      if (this.loading) return;
      const revision = ++this.captchaRevision;
      this.verification.captchaId = this.verification.captchaVal = this.captchaImage = '';
      try {
        const res = await getCode(undefined, true);
        if (!this.visible || revision !== this.captchaRevision || !isCurrentSession(this.snapshot)) return;
        if (!res || res.code !== 200 || !res.data) throw new Error();
        this.verification.captchaId = res.data.captchaId;
        this.captchaImage = res.data.captchaImage;
      } catch (error) {
        if (this.visible && revision === this.captchaRevision) this.$message.error(this.$t('dev.captcha_fail'));
      }
    },
    async verify() {
      if (this.loading || !isCurrentSession(this.snapshot)) return;
      if (!this.verification.userPwd || !this.verification.captchaId || !this.verification.captchaVal) {
        this.$message.error(this.$t('dev.need_pwd_captcha'));
        return;
      }
      const revision = this.revision;
      this.loading = true;
      try {
        const res = await (this.action === 'reset' ? resetSecret : getDevSecret)({ ...this.verification });
        if (!this.visible || revision !== this.revision || !isCurrentSession(this.snapshot)) return;
        if (!res || res.code !== 200) throw new Error((res && res.msg) || this.$t('dev.verify_fail'));
        if (this.action === 'reset') {
          this.$message.success(this.$t('dev.reset_success'));
          this.visible = false;
          this.clearSecrets();
        } else {
          if (!res.data || !res.data.appKey || !res.data.appSecret) throw new Error(this.$t('dev.resp_err'));
          this.appKey = res.data.appKey;
          this.secret = res.data.appSecret;
          this.verified = true;
          this.verification = { userPwd: '', captchaId: '', captchaVal: '' };
          this.captchaImage = '';
        }
      } catch (error) {
        if (this.visible && revision === this.revision && isCurrentSession(this.snapshot)) {
          const data = error.response && error.response.data;
          this.$message.error((data && (data.msg || data.message)) ||
            (error.isAxiosError ? this.$t('dev.service_unavailable') : error.message || this.$t('dev.verify_fail')));
        }
      } finally {
        if (revision === this.revision) {
          this.loading = false;
          if (!this.verified && this.visible) this.loadCaptcha();
        }
      }
    },
    async copy(type) {
      if (!this.verified || !isCurrentSession(this.snapshot)) { this.clearSecrets(); return; }
      try {
        await navigator.clipboard.writeText(type === 'appKey' ? this.appKey : this.secret);
        this.$message.success(this.$t('public.copy_success'));
      } catch (error) { this.$message.error(this.$t('public.copy_fail')); }
    },
  },
};
</script>
  <style lang="scss" scoped>
.main {
  height: 100%;
  padding: 0 10px;
  // width: 100%;
  background-color: #ecf0f3;
  .content {
    // height: calc(100% - 20px);
    background-color: #fff;
    padding: 10px;
    .title {
      padding: 10px 0 20px;
      border-bottom: 1px solid #ebeef5;
    }
    .appKey,
    .secret {
      padding: 10px 0;
      font-size: 14px;

      .info {
        color: #999999;
        padding: 10px 0;
      }
      .option {
        width: 500px;
        justify-content: space-between;
        .btns {
          width: 150px;
          display: flex;
          justify-content: flex-start;
        }
        .key {
          padding: 20px 0;
        }
      }
    }
  }
}

// 弹窗内部元素（scoped 命中模板节点）
.tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 6px;
  color: #e6a23c;
  font-size: 13px;
  line-height: 20px;
  & > i {
    flex: none;
    font-size: 16px;
    line-height: 20px;
  }
}
.fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.captcha-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
  .el-input {
    flex: 1;
  }
}
.captcha-box {
  flex: none;
  width: 110px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover {
    border-color: #0077ff;
  }
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  span {
    font-size: 12px;
    color: #909399;
  }
}
.reveal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.reveal-item {
  display: flex;
  align-items: center;
  gap: 8px;
  .reveal-label {
    flex: none;
    font-size: 13px;
    color: #606266;
  }
  .reveal-value {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    font-family: Consolas, Monaco, monospace;
    font-size: 13px;
    color: #303133;
    word-break: break-all;
  }
  .reveal-copy {
    flex: none;
  }
}
</style>

<style lang="scss">
// 弹窗外壳（custom-class 唯一，非 scoped 以便触达 element 内部结构）
.secret-dialog {
  border-radius: 8px;
  overflow: hidden;
  .el-dialog__header {
    padding: 16px 20px;
    border-bottom: 1px solid #ebeef5;
    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }
  .el-dialog__body {
    padding: 20px;
    color: #606266;
  }
  .el-dialog__footer {
    padding: 12px 20px 20px;
    border-top: 1px solid #ebeef5;
  }
}
</style>